// Phase 6.0b surface (built late, revised) — pending-workspace
// attribution queue for the superadmin User-management page.
//
// When the sync login fan-out discovers a Slack workspace whose
// auth.test email matches no sync_users row, the vault row is parked
// `status=pending_attribution` and never polled. The backend
// (/pending-workspaces) exposes list / create-and-attribute / reject.
//
// Resolution model (operator decision): a pending workspace exists
// precisely because no account matched its Slack email, so the SOLE
// way to attribute it is to CREATE the account for that email — which
// then binds the workspace. There is no attribute-to-arbitrary-user
// path. Each row therefore offers exactly two actions: "Create
// account" (create + attribute, one step-up) or "Reject" (discard).
//
// Embedded as a section on UsersPage (superadmin-gated). "Create
// account" reuses <AddUserModal> with a submitOverride that POSTs the
// combined create-and-attribute endpoint; the email is pre-filled
// from the pending row. Both actions reuse the step-up chrome; a
// wrong password surfaces inline (the API client tags these calls
// X-Step-Up so call() skips the global-logout path on 401).

import { useCallback, useEffect, useState } from "react";
import { Inbox, UserPlus } from "lucide-react";

import {
  ApiError,
  createAndAttributePendingWorkspace,
  listPendingWorkspaces,
  rejectPendingWorkspace,
  type PendingWorkspace,
} from "../lib/api";
import { formatAbsoluteIst, formatRelative } from "../lib/timeFmt";
import { StepUpAuthModal } from "./StepUpAuthModal";
import { AddUserModal } from "./AddUserModal";
import { useToast } from "./ui/Toast";
import { Card } from "./ui/Card";
import { Skeleton } from "./ui/Skeleton";

export type PendingState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; pending: PendingWorkspace[] };

// ---------- container (fetch + orchestration) ----------------------

export function PendingWorkspaces({
  onUsersChanged,
  reloadKey = 0,
}: {
  /** Reload the parent's managed-user list — called after a
   *  "Create account" resolves a pending workspace (a new account
   *  was created) so the user table below stays in sync. */
  onUsersChanged: () => void;
  /** Phase 8.2: bumped by the parent's single Refresh button to
   *  reload this panel too (the panel no longer has its own
   *  refresh control — there's one Refresh for the whole page). */
  reloadKey?: number;
}) {
  const [state, setState] = useState<PendingState>({ kind: "loading" });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const r = await listPendingWorkspaces(signal);
      if (signal?.aborted) return;
      setState({ kind: "ready", pending: r.pending });
    } catch (e) {
      if (signal?.aborted) return;
      const msg =
        e instanceof ApiError ? `${e.message} (HTTP ${e.status})` : String(e);
      setState({ kind: "error", message: msg });
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
    // reloadKey in deps → parent Refresh reloads this panel.
  }, [load, reloadKey]);

  return (
    <PendingWorkspacesView
      state={state}
      onChanged={() => void load()}
      onUsersChanged={onUsersChanged}
    />
  );
}

// ---------- presentational view (pure fn of state) -----------------

export function PendingWorkspacesView({
  state,
  onChanged,
  onUsersChanged,
}: {
  state: PendingState;
  onChanged: () => void;
  onUsersChanged: () => void;
  // Phase 8.2: refresh is owned by the page header now (one button for
  // the whole page); these optional props are kept for the standalone
  // view test's call signature.
  refreshing?: boolean;
  onRefresh?: () => void;
}) {
  const count = state.kind === "ready" ? state.pending.length : null;

  return (
    <section data-testid="pending-workspaces">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-h3 font-semibold flex items-center gap-2">
          Pending workspaces
          {count !== null && count > 0 && (
            <span
              data-testid="pending-count"
              className="inline-flex items-center justify-center min-w-5 px-1.5 py-0.5 bg-theme-warning-bg text-theme-warning-text font-mono text-[10px] rounded-full"
            >
              {count}
            </span>
          )}
        </h2>
      </div>

      {state.kind === "loading" && (
        <Card variant="glass" className="px-5 py-4 rounded-2xl" data-testid="pending-loading">
          <Skeleton variant="text" width="18rem" />
          <Skeleton variant="rectangle" height="2rem" className="mt-2" />
        </Card>
      )}

      {state.kind === "error" && (
        <div
          data-testid="pending-error"
          className="bg-theme-error-bg border border-theme-error-border text-theme-error-text px-4 py-3 text-sm"
        >
          Failed to load pending workspaces: {state.message}
        </div>
      )}

      {state.kind === "ready" && state.pending.length === 0 && (
        <Card variant="glass" className="px-5 py-4 rounded-2xl" data-testid="pending-empty">
          <div className="flex items-center gap-2 font-mono text-xs text-theme-text-tertiary">
            <Inbox className="w-4 h-4" strokeWidth={1.5} aria-hidden />
            No workspaces awaiting attribution. New Slack logins whose email
            matches a user account here activate automatically.
          </div>
        </Card>
      )}

      {state.kind === "ready" && state.pending.length > 0 && (
        <div className="space-y-3" data-testid="pending-list">
          {state.pending.map((pw) => (
            <PendingRow
              key={pw.vault_id}
              pending={pw}
              onChanged={onChanged}
              onUsersChanged={onUsersChanged}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------- one pending row ----------------------------------------

function PendingRow({
  pending,
  onChanged,
  onUsersChanged,
}: {
  pending: PendingWorkspace;
  onChanged: () => void;
  onUsersChanged: () => void;
}) {
  const { toast } = useToast();
  const [showReject, setShowReject] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const label = pending.team_name || pending.team_id;

  const onRejectConfirm = useCallback(
    async (currentPassword: string) => {
      await rejectPendingWorkspace(pending.vault_id, currentPassword);
      setShowReject(false);
      toast({ title: `${label} rejected`, type: "success" });
      onChanged();
    },
    [pending.vault_id, label, toast, onChanged],
  );

  return (
    <Card
      variant="glass"
      className="px-5 py-4"
      data-testid={`pending-row-${pending.vault_id}`}
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="font-sans text-sm text-theme-text-primary truncate">
            {pending.team_name || "(unnamed workspace)"}
          </div>
          <div className="font-mono text-[10px] text-theme-text-muted truncate">
            {pending.team_id}
            {pending.pending_email && (
              <>
                {" · "}
                <span className="text-theme-warning-text">
                  {pending.pending_email}
                </span>{" "}
                (no matching account)
              </>
            )}
            {pending.discovered_at && (
              <span title={formatAbsoluteIst(pending.discovered_at)}>
                {" · discovered "}
                {formatRelative(pending.discovered_at)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid={`pending-create-user-${pending.vault_id}`}
            title={
              pending.pending_email
                ? `Create an account for ${pending.pending_email} and attribute this workspace to it`
                : "Create an account and attribute this workspace to it"
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-theme-accent-bg font-mono text-[10px] uppercase tracking-widest text-theme-accent-link hover:border-theme-accent-link transition-colors duration-150"
          >
            <UserPlus className="w-3 h-3" strokeWidth={1.5} aria-hidden />
            Create account
          </button>
          <button
            type="button"
            onClick={() => setShowReject(true)}
            data-testid={`pending-reject-${pending.vault_id}`}
            className="px-3 py-1.5 border border-theme-error-border font-mono text-[10px] uppercase tracking-widest text-theme-error-text hover:bg-theme-error-bg transition-colors duration-150"
          >
            Reject
          </button>
        </div>
      </div>

      {showCreate && (
        <AddUserModal
          title={`Create account for ${label}`}
          defaultEmail={pending.pending_email ?? ""}
          stepUpDescription={
            <>
              Creating an account for{" "}
              <span className="text-theme-text-primary">
                {pending.pending_email || "this workspace"}
              </span>{" "}
              and attributing{" "}
              <span className="text-theme-text-primary">{label}</span> to it.
              The workspace activates and the sync daemon begins archiving it
              on the next poll. Enter your own password to authorize.
            </>
          }
          submitOverride={(body) =>
            createAndAttributePendingWorkspace(pending.vault_id, body)
          }
          onCreated={(account) => {
            setShowCreate(false);
            toast({
              title: `${label} attributed to ${account.username}`,
              type: "success",
            });
            onChanged(); // workspace is no longer pending — drop it off
            onUsersChanged(); // a new account may exist — refresh the table
          }}
          onClose={() => setShowCreate(false)}
        />
      )}

      {showReject && (
        <StepUpAuthModal
          title="Reject workspace?"
          description={
            <>
              Permanently discard{" "}
              <span className="text-theme-text-primary">{label}</span> — deletes
              its stored session + token and its on-disk session directory. The
              next Slack login would re-discover it. This cannot be undone.
              Enter your own password to authorize.
            </>
          }
          confirmLabel="Reject + discard"
          confirmTone="danger"
          onConfirm={onRejectConfirm}
          onClose={() => setShowReject(false)}
        />
      )}
    </Card>
  );
}
